import React, { useState } from 'react';
import axios from 'axios';
import { Shield, Smartphone, KeyRound, CheckCircle2, Copy, AlertTriangle } from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../../theme';
import { OralzyLogo } from '../../pages/LandingPage';

const card = {
    backgroundColor: theme.surface, borderRadius: '20px',
    padding: '36px 40px', width: '480px', maxWidth: '95vw',
    boxShadow: theme.shadowLg, border: `1px solid ${theme.border}`,
};
const btn = (color = theme.primary) => ({
    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
    backgroundColor: color, color: 'white', fontWeight: '700', fontSize: '0.95rem',
    cursor: 'pointer', marginTop: '8px',
});
const stepDot = (active, done) => ({
    width: '10px', height: '10px', borderRadius: '50%',
    backgroundColor: done ? theme.success : active ? theme.primary : theme.borderLight,
});

export default function AdminSetupWizard({ API_BASE, fingerprint, deviceCtx, onComplete }) {
    const [step, setStep] = useState(1);
    const [err,  setErr]  = useState('');
    const [busy, setBusy] = useState(false);

    // step 1 — credentials
    const [username,  setUsername]  = useState('');
    const [password,  setPassword]  = useState('');
    const [fullName,  setFullName]  = useState('');

    // step 2 — contact
    const [adminMobile, setAdminMobile] = useState('+91');
    const [adminEmail,  setAdminEmail]  = useState('');
    const [otpChannel,  setOtpChannel]  = useState('whatsapp');

    // step 3 — TOTP QR
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [totpSecret, setTotpSecret] = useState('');

    // step 4 — TOTP verify
    const [totpCode, setTotpCode] = useState('');
    const [recoveryCodes, setRecoveryCodes] = useState([]);

    const submitStep1 = () => {
        if (!username || !password || !fullName) return setErr('All fields required');
        if (password.length < 6) return setErr('Password must be 6+ characters');
        setErr(''); setStep(2);
    };

    const submitStep2 = async () => {
        if (!adminMobile || adminMobile.length < 10) return setErr('Valid mobile required (with country code)');
        setErr(''); setBusy(true);
        try {
            const r = await axios.post(`${API_BASE}/security/setup-admin`, {
                username, password, fullName, adminMobile, adminEmail, otpChannel,
            });
            setQrDataUrl(r.data.qrDataUrl);
            setTotpSecret(r.data.totpSecret);
            setStep(3);
        } catch (e) {
            setErr(e.response?.data?.error || 'Setup failed');
        }
        setBusy(false);
    };

    const submitStep4 = async () => {
        if (!totpCode || totpCode.length !== 6) return setErr('Enter 6-digit code');
        setErr(''); setBusy(true);
        try {
            const r = await axios.post(`${API_BASE}/security/setup-verify-totp`, {
                totp: totpCode, fingerprint,
                deviceName: 'Admin PC',
                userAgent: deviceCtx.userAgent, platform: deviceCtx.platform,
            });
            setRecoveryCodes(r.data.recoveryCodes || []);
            setStep(5);
        } catch (e) {
            setErr(e.response?.data?.error || 'Verification failed');
        }
        setBusy(false);
    };

    const stepDots = (
        <div style={{display:'flex', gap:'8px', justifyContent:'center', marginBottom:'24px'}}>
            {[1,2,3,4,5].map(n => <div key={n} style={stepDot(n===step, n<step)}/>)}
        </div>
    );

    return (
        <div style={{minHeight:'100vh', backgroundColor:theme.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
            <div style={card}>
                <div style={{textAlign:'center', marginBottom:'24px'}}>
                    <OralzyLogo height={44}/>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:'8px', marginTop:'14px'}}>
                        <Shield size={18} color={theme.primary}/>
                        <h2 style={{margin:0, color:theme.navy, fontSize:'1.15rem', fontWeight:'700'}}>Admin Security Setup</h2>
                    </div>
                    <p style={{margin:'4px 0 0', color:theme.textMuted, fontSize:'0.8rem'}}>One-time setup. This PC will become the admin device.</p>
                </div>

                {stepDots}

                {err && (
                    <div style={{padding:'10px 14px', borderRadius:'8px', backgroundColor:'#fef2f2', color:theme.danger, fontSize:'0.85rem', marginBottom:'14px', display:'flex', gap:'8px', alignItems:'center'}}>
                        <AlertTriangle size={16}/>{err}
                    </div>
                )}

                {step === 1 && (<>
                    <h3 style={{margin:'0 0 14px', color:theme.navy, fontSize:'1rem'}}>1. Admin credentials</h3>
                    <label style={labelStyle}>Full name *</label>
                    <input value={fullName} onChange={e=>setFullName(e.target.value)} style={inputStyle} placeholder="Dr Sumit"/>
                    <label style={labelStyle}>Username *</label>
                    <input value={username} onChange={e=>setUsername(e.target.value.toLowerCase())} style={inputStyle} placeholder="admin"/>
                    <label style={labelStyle}>Password * (6+ chars)</label>
                    <input type="password" value={password} onChange={e=>setPassword(e.target.value)} style={inputStyle}/>
                    <button onClick={submitStep1} style={btn()}>Continue</button>
                </>)}

                {step === 2 && (<>
                    <h3 style={{margin:'0 0 14px', color:theme.navy, fontSize:'1rem'}}>2. Contact for OTP & alerts</h3>
                    <label style={labelStyle}>Admin mobile (with country code) *</label>
                    <input value={adminMobile} onChange={e=>setAdminMobile(e.target.value)} style={inputStyle} placeholder="+9198XXXXXXXX"/>
                    <label style={labelStyle}>Email (for security alerts)</label>
                    <input value={adminEmail} onChange={e=>setAdminEmail(e.target.value)} style={inputStyle} placeholder="admin@clinic.com"/>
                    <label style={labelStyle}>OTP delivery channel</label>
                    <div style={{display:'flex', gap:'8px', marginBottom:'12px'}}>
                        {[
                            { v:'whatsapp', l:'WhatsApp' },
                            { v:'sms', l:'SMS' },
                            { v:'both', l:'Both' },
                        ].map(opt => (
                            <button key={opt.v} onClick={()=>setOtpChannel(opt.v)}
                                style={{flex:1, padding:'10px', borderRadius:'8px', border:`1px solid ${otpChannel===opt.v ? theme.primary : theme.border}`,
                                    backgroundColor: otpChannel===opt.v ? theme.primary+'18' : 'white',
                                    color: otpChannel===opt.v ? theme.primary : theme.textMuted,
                                    fontWeight:'600', cursor:'pointer', fontSize:'0.85rem'}}>{opt.l}</button>
                        ))}
                    </div>
                    <button onClick={submitStep2} disabled={busy} style={btn()}>{busy ? 'Setting up…' : 'Continue'}</button>
                </>)}

                {step === 3 && (<>
                    <h3 style={{margin:'0 0 14px', color:theme.navy, fontSize:'1rem'}}>3. Scan with Google Authenticator</h3>
                    <p style={{fontSize:'0.85rem', color:theme.textMuted, margin:'0 0 12px'}}>Open the Google Authenticator app on your phone, tap <strong>+</strong>, then <strong>Scan QR code</strong>.</p>
                    <div style={{textAlign:'center', backgroundColor:'#f8fafc', padding:'14px', borderRadius:'12px', margin:'8px 0'}}>
                        {qrDataUrl ? <img src={qrDataUrl} alt="TOTP QR" style={{maxWidth:'200px'}}/> : 'Loading…'}
                    </div>
                    <p style={{fontSize:'0.78rem', color:theme.textMuted, margin:'8px 0 4px'}}>Can't scan? Enter this secret manually:</p>
                    <div onClick={()=>{ navigator.clipboard.writeText(totpSecret); }} style={{padding:'8px 12px', backgroundColor:'#f1f5f9', borderRadius:'8px', fontFamily:'monospace', fontSize:'0.82rem', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px'}}>
                        <Copy size={14}/> {totpSecret}
                    </div>
                    <button onClick={()=>setStep(4)} style={btn()}>I've added it to Authenticator</button>
                </>)}

                {step === 4 && (<>
                    <h3 style={{margin:'0 0 14px', color:theme.navy, fontSize:'1rem'}}>4. Verify the 6-digit code</h3>
                    <p style={{fontSize:'0.85rem', color:theme.textMuted, margin:'0 0 12px'}}>Enter the current code from Google Authenticator.</p>
                    <input value={totpCode} onChange={e=>setTotpCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                        style={{...inputStyle, fontSize:'1.4rem', textAlign:'center', letterSpacing:'0.5em', fontFamily:'monospace'}}
                        placeholder="000000" maxLength={6}/>
                    <button onClick={submitStep4} disabled={busy} style={btn()}>{busy ? 'Verifying…' : 'Verify & Complete Setup'}</button>
                </>)}

                {step === 5 && (<>
                    <div style={{textAlign:'center', marginBottom:'16px'}}>
                        <CheckCircle2 size={48} color={theme.success}/>
                        <h3 style={{margin:'8px 0 4px', color:theme.navy}}>Setup Complete!</h3>
                        <p style={{margin:0, color:theme.textMuted, fontSize:'0.85rem'}}>This PC is now your admin device.</p>
                    </div>
                    <div style={{padding:'12px 14px', backgroundColor:'#fff7ed', border:'1px solid #fed7aa', borderRadius:'10px', marginBottom:'12px'}}>
                        <div style={{fontWeight:'700', color:'#9a3412', fontSize:'0.86rem', marginBottom:'4px'}}>⚠ Save these recovery codes</div>
                        <div style={{fontSize:'0.78rem', color:'#7c2d12'}}>If you lose your phone, use any one of these to regain admin access. Each code works only once.</div>
                    </div>
                    <div style={{backgroundColor:'#f8fafc', padding:'12px', borderRadius:'10px', marginBottom:'12px'}}>
                        <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px', fontFamily:'monospace', fontSize:'0.85rem'}}>
                            {recoveryCodes.map((c,i) => <div key={i} style={{padding:'4px 8px', backgroundColor:'white', borderRadius:'6px', textAlign:'center'}}>{c}</div>)}
                        </div>
                        <button onClick={()=>{ navigator.clipboard.writeText(recoveryCodes.join('\n')); }} style={{marginTop:'8px', width:'100%', padding:'6px', borderRadius:'6px', border:`1px solid ${theme.border}`, backgroundColor:'white', cursor:'pointer', fontSize:'0.78rem', fontWeight:'600'}}>
                            <Copy size={12} style={{verticalAlign:'middle', marginRight:'4px'}}/>Copy all
                        </button>
                    </div>
                    <button onClick={()=>onComplete && onComplete()} style={btn(theme.success)}>I've saved my recovery codes — Continue to Login</button>
                </>)}
            </div>
        </div>
    );
}
